/**
 * AudioMixer — Centralized audio routing & auto-mixing for NeuroPsyMap
 *
 * All sound sources route through this mixer into the AudioEngine's bus system.
 * Provides: bus-aware playback, auto-ducking, dynamic range control, cooldowns.
 *
 * Bus routing:
 *   sfx:      node growth buzz, chain zaps, sparkles, UI interactions
 *   voice:    musical notes, creature voice, phonation, harmonic patterns
 *   ambience: heart hum, background drones, energy fields
 *   breathing: breath whoosh, respiratory cycle sounds
 */

import { AudioEngine, AudioBusName } from './audio/AudioEngine';

export type MixerSoundType = 'buzz' | 'sparkle' | 'heartHum' | 'breath' | 'musicalNote' | 'zap' | 'voice' | 'discovery';

const SOUND_TO_BUS: Record<MixerSoundType, AudioBusName> = {
  buzz:        'sfx',
  sparkle:     'sfx',
  zap:         'sfx',
  heartHum:    'ambience',
  breath:      'breathing',
  musicalNote: 'voice',
  voice:       'voice',
  discovery:   'voice',
};

export class AudioMixer {
  private engine: AudioEngine;
  private cooldowns: Map<string, number> = new Map();
  private activeSources: Set<AudioBufferSourceNode> = new Set();

  constructor() {
    this.engine = AudioEngine.getInstance();
  }

  get context(): AudioContext { return this.engine.context; }
  get isRunning(): boolean { return this.engine.context.state === 'running'; }

  /** Route an existing AudioNode to the correct bus */
  routeToBus(node: AudioNode, type: MixerSoundType): void {
    const bus = SOUND_TO_BUS[type];
    this.engine.connectToBus(node, bus);
  }

  /** Create a gain node pre-routed to the correct bus */
  createGain(type: MixerSoundType, initialValue: number = 1.0): GainNode {
    const gain = this.engine.acquireGain();
    gain.gain.value = initialValue;
    const bus = SOUND_TO_BUS[type];
    gain.connect(this.engine.getBusGain(bus));
    return gain;
  }

  /** Play a buffer through the correct bus with auto-ducking and click-free envelopes */
  playBuffer(
    buffer: AudioBuffer,
    type: MixerSoundType,
    volume: number = 0.5,
    pan: number = 0,
    when?: number
  ): AudioBufferSourceNode {
    const ctx = this.engine.context;
    const now = when ?? ctx.currentTime;
    const gain = this.engine.acquireGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(Math.max(0.0001, volume), now + 0.005);

    const panner = this.engine.acquirePanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(panner);
    panner.connect(gain);
    this.engine.connectToBus(gain, SOUND_TO_BUS[type]);
    source.start(now);

    // Cleanup on end
    source.onended = () => {
      this.activeSources.delete(source);
      try { source.disconnect(); } catch { /* already disconnected */ }
      try { panner.disconnect(); } catch { /* already disconnected */ }
      this.engine.releaseGain(gain);
      this.engine.releasePanner(panner);
    };

    this.activeSources.add(source);
    return source;
  }

  /** Create a sustained tone through the correct bus (returns stop function) */
  createTone(
    frequency: number,
    type: MixerSoundType,
    waveform: OscillatorType = 'sine',
    volume: number = 0.3
  ): { stop: () => void; oscillator: OscillatorNode; gain: GainNode } {
    const ctx = this.engine.context;
    const osc = ctx.createOscillator();
    osc.type = waveform;
    osc.frequency.value = frequency;

    const gain = this.engine.acquireGain();
    gain.gain.value = 0.0001;
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);

    osc.connect(gain);
    this.engine.connectToBus(gain, SOUND_TO_BUS[type]);
    osc.start();

    return {
      oscillator: osc,
      gain,
      stop: () => {
        const t = ctx.currentTime;
        gain.gain.cancelScheduledValues(t);
        gain.gain.setValueAtTime(gain.gain.value, t);
        gain.gain.linearRampToValueAtTime(0.0001, t + 0.05);
        osc.stop(t + 0.06);
        osc.onended = () => {
          try { osc.disconnect(); } catch { /* ok */ }
          this.engine.releaseGain(gain);
        };
      }
    };
  }

  /** Check cooldown before playing (prevents audio flooding) */
  canPlay(key: string, cooldownMs: number = 50): boolean {
    const now = performance.now();
    const last = this.cooldowns.get(key) ?? 0;
    if (now - last < cooldownMs) return false;
    this.cooldowns.set(key, now);
    return true;
  }

  /** Set master volume (0-1) */
  setMasterVolume(v: number): void {
    this.engine.masterGain.gain.value = Math.max(0, Math.min(1, v));
  }

  /** Set bus volume (0-2) */
  setBusVolume(bus: AudioBusName, v: number): void {
    const gain = this.engine.getBusGain(bus).gain;
    gain.value = Math.max(0, Math.min(2, v));
  }

  /** Temporarily duck a bus (e.g., when voice fires, duck ambience) */
  duckBus(target: AudioBusName, level: number, duration: number): void {
    this.engine.duckBus(target, level, duration);
  }

  /** Stop and release all active sources */
  stopAll(): void {
    for (const src of this.activeSources) {
      try { src.stop(); } catch { /* already stopped */ }
    }
    this.activeSources.clear();
  }
}

// Singleton
let _mixer: AudioMixer | null = null;
export function getAudioMixer(): AudioMixer {
  if (!_mixer) _mixer = new AudioMixer();
  return _mixer;
}
